/* eslint-disable no-empty */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LogOut, Menu, X, ChevronRight } from 'lucide-react';
import { useCatering } from './CateringContext';
import { CATERING_MODULES } from './catering-modules';

const ROOT = '/gate33-catering-and-operations-training';

// =================================================================
// TOP BAR
// =================================================================
export const CateringTopBar = () => {
  const { logout } = useCatering();
  return (
    <header
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        background: 'rgba(245, 240, 230, 0.85)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--g33-ligne)',
      }}
    >
      <div style={{
        maxWidth: 1280, margin: '0 auto', padding: '16px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 24,
      }}>
        <Link
          to={ROOT}
          style={{
            fontFamily: 'var(--g33-font-display)', fontSize: 22,
            fontWeight: 600, letterSpacing: '-0.01em', textDecoration: 'none',
            color: 'var(--g33-noir)',
          }}
        >
          GATE <span style={{ color: 'var(--g33-or)' }}>33</span>
        </Link>

        <div
          className="g33-eyebrow"
          style={{
            display: 'none', // simple TopBar — module nav lives in the SideRail
          }}
        >
          Operations &amp; Catering
        </div>

        <button
          onClick={logout}
          className="g33-btn-ghost"
          data-testid="catering-logout"
          style={{ padding: '8px 0' }}
        >
          <LogOut size={14} /> Logout
        </button>
      </div>
    </header>
  );
};

// =================================================================
// SIDE RAIL — flat list of the 10 modules (no day grouping)
// =================================================================
export const CateringSideRail = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        aria-label="Toggle module list"
        data-testid="catering-rail-toggle"
        style={{
          position: 'fixed', top: 76, left: 16, zIndex: 45,
          width: 40, height: 40, background: 'var(--g33-cream-soft)',
          border: '1px solid var(--g33-ligne)', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {open ? <X size={16} /> : <Menu size={16} />}
      </button>

      {open && (
        <aside
          style={{
            position: 'fixed', top: 65, left: 0, bottom: 0, zIndex: 44,
            width: 340, background: 'var(--g33-cream-soft)',
            borderRight: '1px solid var(--g33-ligne)',
            overflowY: 'auto', padding: '24px 32px 32px 64px',
          }}
        >
          <div className="g33-eyebrow" style={{ marginBottom: 16 }}>10 Modules</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {CATERING_MODULES.map((m) => {
              const active = pathname.includes(`/module/${m.slug}`);
              return (
                <li key={m.slug} style={{ marginBottom: 4 }}>
                  <Link
                    to={`${ROOT}/module/${m.slug}`}
                    onClick={() => setOpen(false)}
                    data-testid={`catering-rail-module-${m.n}`}
                    style={{
                      display: 'flex', alignItems: 'baseline', gap: 12,
                      padding: '10px 0', textDecoration: 'none',
                      color: active ? 'var(--g33-noir)' : 'var(--g33-charcoal)',
                      fontFamily: 'var(--g33-font-body)', fontSize: 14,
                      borderLeft: active ? '2px solid var(--g33-or)' : '2px solid transparent',
                      paddingLeft: 12,
                    }}
                  >
                    <span style={{
                      fontFamily: 'var(--g33-font-display)',
                      color: 'var(--g33-or)', fontSize: 14,
                      minWidth: 24,
                    }}>
                      {String(m.n).padStart(2, '0')}
                    </span>
                    <span style={{ flex: 1, lineHeight: 1.35 }}>{m.title}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </aside>
      )}
    </>
  );
};

// =================================================================
// BOTTOM DOCK — prev / next module nav
// =================================================================
export const CateringBottomDock = ({ current }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const idx = CATERING_MODULES.findIndex((m) => m.slug === current.slug);
  const prev = idx > 0 ? CATERING_MODULES[idx - 1] : null;
  const next = idx < CATERING_MODULES.length - 1 ? CATERING_MODULES[idx + 1] : null;

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40,
      background: 'rgba(245, 240, 230, 0.92)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderTop: '1px solid var(--g33-ligne)',
    }}>
      <div style={{
        maxWidth: 1280, margin: '0 auto', padding: '14px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 24,
      }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          {prev ? (
            <Link
              to={`${ROOT}/module/${prev.slug}`}
              data-testid="catering-prev-module"
              style={{
                textDecoration: 'none', color: 'var(--g33-charcoal)',
                display: 'flex', alignItems: 'center', gap: 10,
              }}
            >
              <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} />
              <div>
                <div className="g33-eyebrow">Previous</div>
                <div style={{ fontSize: 13, marginTop: 2 }}>{prev.title}</div>
              </div>
            </Link>
          ) : <span />}
        </div>

        <div className="g33-eyebrow" style={{ textAlign: 'center' }}>
          Module {String(current.n).padStart(2, '0')} of 10
        </div>

        <div style={{ minWidth: 0, flex: 1, textAlign: 'right' }}>
          {next ? (
            <Link
              to={`${ROOT}/module/${next.slug}`}
              data-testid="catering-next-module"
              style={{
                textDecoration: 'none', color: 'var(--g33-charcoal)',
                display: 'inline-flex', alignItems: 'center', gap: 10,
              }}
            >
              <div>
                <div className="g33-eyebrow">Next</div>
                <div style={{ fontSize: 13, marginTop: 2 }}>{next.title}</div>
              </div>
              <ChevronRight size={14} />
            </Link>
          ) : <span />}
        </div>
      </div>
    </div>
  );
};
